import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Merkt sich die Scroll-Position je Seite (Pfad + Query) und stellt sie beim
 * Zurückkehren wieder her — z. B. Turnus → Klienten → Turnus landet wieder
 * an derselben Stelle statt oben.
 *
 * Gespeichert nur im Browser-Tab (sessionStorage, Fallback Arbeitsspeicher).
 * Weil Seiten ihre Daten erst nach dem Laden nachreichen, wird so lange
 * erneut gescrollt, bis die Seite hoch genug ist — höchstens STOP_AFTER_MS
 * und sofort abgebrochen, sobald der Benutzer selbst scrollt.
 */
const STORAGE_KEY = 'scrollMemory.v1';
const STOP_AFTER_MS = 4000;
const memory = new Map<string, number>();

const readStore = (): Record<string, number> => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const persist = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(memory)));
  } catch {
    /* privater Modus / Speicher voll — dann eben nur im Arbeitsspeicher */
  }
};

Object.entries(readStore()).forEach(([key, value]) => memory.set(key, Number(value) || 0));

const ScrollMemory: React.FC = () => {
  const location = useLocation();
  const key = `${location.pathname}${location.search}`;
  const restoringRef = useRef(false);
  const keyRef = useRef(key);

  // Der Browser soll nicht selbst (falsch) wiederherstellen.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => { window.history.scrollRestoration = previous; };
    }
    return undefined;
  }, []);

  // Position der aktuellen Seite laufend mitschreiben.
  useEffect(() => {
    let frame = 0;
    let saveTimer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      if (restoringRef.current || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        memory.set(keyRef.current, window.scrollY);
        clearTimeout(saveTimer);
        saveTimer = setTimeout(persist, 300);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
      clearTimeout(saveTimer);
    };
  }, []);

  // Seitenwechsel: gemerkte Position wiederherstellen, sonst nach oben.
  useLayoutEffect(() => {
    keyRef.current = key;
    const target = memory.get(key) || 0;
    if (target <= 0) {
      window.scrollTo(0, 0);
      return undefined;
    }

    restoringRef.current = true;
    const started = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      restoringRef.current = false;
      clearTimeout(timer);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
      window.removeEventListener('mousedown', stop);
    };
    const attempt = () => {
      window.scrollTo(0, target);
      const reached = Math.abs(window.scrollY - target) < 2;
      if (reached || Date.now() - started > STOP_AFTER_MS) {
        stop();
        return;
      }
      timer = setTimeout(attempt, 100);
    };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    window.addEventListener('mousedown', stop);
    attempt();
    return stop;
  }, [key]);

  return null;
};

export default ScrollMemory;
